export type RoleScope = 'internal' | 'customer';

/**
 * Get the prefix for a role name based on scope and isShared.
 * - Internal roles: always sf_
 * - Shared customer roles (isShared=true or no companyId): default_
 * - Customer-specific roles (isShared=false, has companyId): no prefix
 */
export function getRolePrefix(
  scope: RoleScope,
  companyId?: string | null,
  isShared?: boolean,
): string {
  if (scope === 'internal') return 'sf_';
  if (isShared || !companyId) return 'default_';
  // Customer-specific roles have no prefix — uniqueness is per companyId
  return '';
}

/** Build the full role name from user input + prefix */
export function buildRoleName(
  userInput: string,
  scope: RoleScope,
  companyId?: string | null,
  isShared?: boolean,
): string {
  const prefix = getRolePrefix(scope, companyId, isShared);
  return `${prefix}${userInput}`;
}

/**
 * Parse a role name back into its parts (for edit form pre-population).
 * Customer-specific roles have no prefix — the full name is the editable part.
 */
export function parseRoleName(
  name: string,
  _companyId?: string | null,
): {
  prefix: string;
  userPart: string;
  scope: RoleScope;
} {
  if (name.startsWith('sf_')) {
    return { prefix: 'sf_', userPart: name.slice('sf_'.length), scope: 'internal' };
  }
  if (name.startsWith('default_')) {
    return { prefix: 'default_', userPart: name.slice('default_'.length), scope: 'customer' };
  }
  // Customer-specific role (no prefix) — return full name as editable part
  return { prefix: '', userPart: name, scope: 'customer' };
}

/** Reserved prefixes that customer-specific role names must not start with */
const RESERVED_PREFIXES = ['sf_', 'default_'] as const;

/**
 * Validate that a customer-specific role name doesn't use reserved prefixes.
 * Returns an error message if invalid, or null if OK.
 */
export function validateCustomerRoleName(name: string): string | null {
  for (const prefix of RESERVED_PREFIXES) {
    if (name.startsWith(prefix)) {
      return `Customer-specific role names cannot start with "${prefix}" — this prefix is reserved.`;
    }
  }
  return null;
}
